import { supabase } from '../supabaseClient';
import { sanitizeInputByType, sanitizeStructuredInput } from '../helpers/formatHelpers';

const CHAT_REQUEST_COLUMNS = 'request_key, doc, status, created_at, attempt_count, failed_attempts, timeout_attempts, total_duration_ms';

const isMissingRelationError = (error) => {
  const code = String(error?.code || '').trim();
  const message = String(error?.message || '').toLowerCase();
  return code === '42P01'
    || (message.includes('relation')
      && (message.includes('ai_requests') || message.includes('ai_request_attempts')));
};

export async function apiGetNextRequestKeyForDoc({ docId } = {}) {
  const normalizedDocId = Number(docId);
  if (!Number.isFinite(normalizedDocId) || normalizedDocId <= 0) return null;

  const pageSize = 1000;
  let from = 0;
  let maxIndex = 0;
  const matcher = new RegExp(`^${normalizedDocId}_(\\d+)$`);

  while (true) {
    const { data, error } = await supabase
      .from('ai_requests')
      .select('request_key')
      .eq('doc', normalizedDocId)
      .range(from, from + pageSize - 1);

    if (error) {
      if (isMissingRelationError(error)) return `${normalizedDocId}_1`;
      throw error;
    }

    const rows = Array.isArray(data) ? data : [];
    for (const row of rows) {
      const key = String(row?.request_key || '').trim();
      const match = key.match(matcher);
      if (!match?.[1]) continue;
      const nextValue = Number(match[1]);
      // Ignore legacy timestamp-like values so new keys can start at doc_1/doc_2.
      const isReasonableSequentialIndex = Number.isFinite(nextValue)
        && nextValue > 0
        && nextValue <= 1000000;
      if (isReasonableSequentialIndex && nextValue > maxIndex) {
        maxIndex = nextValue;
      }
    }

    if (rows.length < pageSize) break;
    from += pageSize;
    if (from > 20000) break;
  }

  return `${normalizedDocId}_${maxIndex + 1}`;
}

export async function apiInsertChatRequest({
  chatDocId,
  requestId,
  requestKey,
  chatMode = null,
  intentKey = null,
  requestedModel = null,
  responseModel = null,
  usage = null,
  cost = null,
  activeProjectId = null,
  billingOwnerUserId = null,
  initiatorUserId = null,
  attemptCount = null,
  failedAttemptCount = null,
  timeoutAttemptCount = null,
  totalDurationMs = null,
  attemptsIndex = null,
  attemptSummary = null,
  providerName = 'openrouter',
  status = 'success',
  errorMessage = null,
  feedbackScore = null,
  metadata = null
} = {}) {
  const docId = Number(chatDocId);
  if (!Number.isFinite(docId) || docId <= 0) return null;

  const normalizedRequestKey = sanitizeInputByType(requestKey, 'identifier', { maxLength: 120 });
  if (!normalizedRequestKey) return null;

  const normalizedFeedback = Number(feedbackScore);

  const payload = {
    doc: docId,
    request_key: normalizedRequestKey,
    chat_mode: sanitizeInputByType(chatMode, 'identifier', { maxLength: 40 }) || null,
    intent_key: sanitizeInputByType(intentKey, 'identifier', { maxLength: 40 }) || null,
    response_model: sanitizeInputByType(responseModel, 'title', { maxLength: 120 }) || null,
    project_id: Number.isFinite(Number(activeProjectId)) ? Number(activeProjectId) : null,
    project_owner_id: sanitizeInputByType(billingOwnerUserId, 'identifier', { maxLength: 120 }) || null,
    user_id: sanitizeInputByType(initiatorUserId, 'identifier', { maxLength: 120 }) || null,
    input_tokens: Number(usage?.promptTokens || 0) || 0,
    output_tokens: Number(usage?.completionTokens || 0) || 0,
    total_tokens: Number(usage?.totalTokens || 0) || 0,
    input_cost: Number.isFinite(Number(cost?.promptCostUsd)) ? Number(cost.promptCostUsd) : null,
    output_cost: Number.isFinite(Number(cost?.completionCostUsd)) ? Number(cost.completionCostUsd) : null,
    total_cost: Number.isFinite(Number(cost?.totalCostUsd))
      ? Number(cost.totalCostUsd)
      : null,
    attempt_count: Number.isFinite(Number(attemptCount)) ? Number(attemptCount) : 0,
    failed_attempts: Number.isFinite(Number(failedAttemptCount)) ? Number(failedAttemptCount) : 0,
    timeout_attempts: Number.isFinite(Number(timeoutAttemptCount)) ? Number(timeoutAttemptCount) : 0,
    total_duration_ms: Number.isFinite(Number(totalDurationMs)) ? Number(totalDurationMs) : 0,
    attempts_summary: attemptSummary && typeof attemptSummary === 'object'
      ? sanitizeStructuredInput(attemptSummary, { maxDepth: 10, maxStringLength: 2000 })
      : { stats: {}, attempts: [] },
    status: sanitizeInputByType(status, 'identifier', { maxLength: 40 }) || 'success',
    error_message: sanitizeInputByType(errorMessage, 'text', { maxLength: 1000 }).trim() || null,
    feedback_score: Number.isFinite(normalizedFeedback) && (normalizedFeedback === -1 || normalizedFeedback === 1)
      ? normalizedFeedback
      : null
  };

  const { data, error } = await supabase
    .from('ai_requests')
    .upsert([payload], { onConflict: 'request_key' })
    .select(CHAT_REQUEST_COLUMNS)
    .maybeSingle();

  if (error) {
    if (isMissingRelationError(error)) return null;
    throw error;
  }

  return data || null;
}

export async function apiInsertChatRequestAttempt({
  requestHash = null,
  attemptHash = null,
  chatDocId = null,
  activeProjectId = null,
  billingOwnerUserId = null,
  initiatorUserId = null,
  attemptIndex,
  stageName = null,
  callKind = null,
  providerName = 'openrouter',
  providerRequestId = null,
  candidateModel = null,
  responseModel = null,
  transport = null,
  status = 'started',
  isTimeout = false,
  durationMs = null,
  usage = null,
  cost = null,
  errorCode = null,
  errorMessage = null,
  requestExcerpt = null,
  responseExcerpt = null,
  requestPayloadHash = null,
  responsePayloadHash = null,
  metadata = null
} = {}) {
  const normalizedRequestHash = sanitizeInputByType(requestHash, 'identifier', { maxLength: 120 });
  const normalizedAttemptHash = sanitizeInputByType(attemptHash, 'identifier', { maxLength: 120 });
  const normalizedAttemptIndex = Number(attemptIndex);
  if (!normalizedRequestHash) return null;
  if (!normalizedAttemptHash) return null;
  if (!Number.isFinite(normalizedAttemptIndex) || normalizedAttemptIndex < 1) return null;

  const payload = {
    request_key: normalizedRequestHash,
    attempt_key: normalizedAttemptHash,
    attempt_index: normalizedAttemptIndex,
    stage_name: sanitizeInputByType(stageName, 'identifier', { maxLength: 60 }) || null,
    provider_name: (sanitizeInputByType(providerName || 'openrouter', 'identifier', { maxLength: 60 }) || 'openrouter').toLowerCase(),
    provider_request_id: sanitizeInputByType(providerRequestId, 'identifier', { maxLength: 160 }) || null,
    candidate_model: sanitizeInputByType(candidateModel, 'title', { maxLength: 120 }) || null,
    response_model: sanitizeInputByType(responseModel, 'title', { maxLength: 120 }) || null,
    status: sanitizeInputByType(status, 'identifier', { maxLength: 40 }) || 'started',
    duration_ms: Number.isFinite(Number(durationMs)) ? Number(durationMs) : null,
    input_tokens: Number(usage?.promptTokens || 0) || 0,
    output_tokens: Number(usage?.completionTokens || 0) || 0,
    total_tokens: Number(usage?.totalTokens || 0) || 0,
    input_cost: Number.isFinite(Number(cost?.promptCostUsd)) ? Number(cost.promptCostUsd) : null,
    output_cost: Number.isFinite(Number(cost?.completionCostUsd)) ? Number(cost.completionCostUsd) : null,
    total_cost: Number.isFinite(Number(cost?.totalCostUsd))
      ? Number(cost.totalCostUsd)
      : null,
    error_code: sanitizeInputByType(errorCode, 'identifier', { maxLength: 80 }) || null,
    error_message: sanitizeInputByType(errorMessage, 'text', { maxLength: 1000 }).trim() || null,
  };

  const { data, error } = await supabase
    .from('ai_request_attempts')
    .upsert([payload], { onConflict: 'attempt_key' })
    .select('attempt_key, request_key, attempt_index, status, created_at')
    .maybeSingle();

  if (error) {
    if (isMissingRelationError(error)) return null;
    throw error;
  }

  return data || null;
}

export async function apiSetChatRequestFeedback({
  chatDocId,
  requestKey,
  feedbackScore
} = {}) {
  const docId = Number(chatDocId);
  if (!Number.isFinite(docId) || docId <= 0) return null;

  const normalizedRequestKey = sanitizeInputByType(requestKey, 'identifier', { maxLength: 120 });
  if (!normalizedRequestKey) return null;

  const normalizedScore = Number(feedbackScore);
  if (!Number.isFinite(normalizedScore) || (normalizedScore !== -1 && normalizedScore !== 1)) return null;

  const payload = {
    feedback_score: normalizedScore,
    feedback_at: new Date().toISOString()
  };

  const { data, error } = await supabase
    .from('ai_requests')
    .update(payload)
    .eq('doc', docId)
    .eq('request_key', normalizedRequestKey)
    .select(CHAT_REQUEST_COLUMNS + ', feedback_score, feedback_at')
    .maybeSingle();

  if (error) {
    if (isMissingRelationError(error)) return null;
    throw error;
  }

  return data || null;
}
